import React, { useEffect, useState } from 'react';
import api from '../api';
import { useSortedPaginated, SortableTh, Pagination } from '../components/TableControls';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ username: '', password: '', full_name: '', role: 'manager', store_ids: [] });
  const [msg, setMsg] = useState('');
  const { paginated, sortKey, sortDir, setSort, page, setPage, perPage, setPerPage, total } = useSortedPaginated(users, 'full_name');

  const load = () => api.get('/users').then(r => setUsers(r.data));
  useEffect(() => {
    load();
    api.get('/stores').then(r => setStores(r.data));
  }, []);

  const openCreate = () => {
    setEditUser(null);
    setForm({ username: '', password: '', full_name: '', role: 'manager', store_ids: [] });
    setShowModal(true);
    setMsg('');
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({ username: u.username, password: '', full_name: u.full_name, role: u.role, store_ids: u.store_ids });
    setShowModal(true);
    setMsg('');
  };

  const toggleStore = (storeId) => {
    setForm(prev => ({
      ...prev,
      store_ids: prev.store_ids.includes(storeId)
        ? prev.store_ids.filter(id => id !== storeId)
        : [...prev.store_ids, storeId],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    setMsg('');
    try {
      if (editUser) {
        const payload = {
          full_name: form.full_name,
          role: form.role,
          store_ids: form.store_ids,
        };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editUser.id}`, payload);
      } else {
        await api.post('/users', form);
      }
      setShowModal(false);
      load();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (u) => {
    if (!window.confirm(`¿Eliminar usuario "${u.full_name}"?`)) return;
    setMsg('');
    try {
      const res = await api.delete(`/users/${u.id}`);
      setMsg(res.data.message);
      load();
    } catch (err) {
      setMsg(err.response?.data?.detail || 'Error');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>Usuarios</h1>
        <button className="btn btn-primary" onClick={openCreate}>Agregar Usuario</button>
      </div>

      {msg && !showModal && <div className={`alert ${msg.includes('archived') ? 'alert-warning' : msg.includes('deleted') ? 'alert-success' : 'alert-danger'}`}>{msg}</div>}

      <div className="card">
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <SortableTh colKey="username" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Usuario</SortableTh>
                <SortableTh colKey="full_name" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Nombre Completo</SortableTh>
                <SortableTh colKey="role" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Rol</SortableTh>
                <th>Tiendas</th>
                <SortableTh colKey="is_active" sortKey={sortKey} sortDir={sortDir} onSort={setSort}>Estado</SortableTh>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(u => (
                <tr key={u.id}>
                  <td>{u.username}</td>
                  <td>{u.full_name}</td>
                  <td><span className={`badge ${u.role === 'admin' ? 'badge-info' : 'badge-success'}`}>{u.role}</span></td>
                  <td>{u.store_ids.map(sid => stores.find(s => s.id === sid)?.name).filter(Boolean).join(', ') || 'Todas'}</td>
                  <td><span className={`badge ${u.is_active ? 'badge-success' : 'badge-danger'}`}>{u.is_active ? 'Activo' : 'Inactivo'}</span></td>
                  <td>
                    <button className="btn btn-sm btn-secondary" onClick={() => openEdit(u)}>Editar</button>
                    {' '}
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(u)}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={page} perPage={perPage} total={total} onPage={setPage} onPerPage={setPerPage} />
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>{editUser ? 'Editar Usuario' : 'Agregar Usuario'}</h2>
            {msg && <div className="alert alert-danger">{msg}</div>}
            <form onSubmit={save}>
              {!editUser && (
                <div className="form-group">
                  <label>Usuario</label>
                  <input value={form.username} onChange={e => setForm({...form, username: e.target.value})} required />
                </div>
              )}
              <div className="form-group">
                <label>{editUser ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña'}</label>
                <input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} required={!editUser} />
              </div>
              <div className="form-group">
                <label>Nombre Completo</label>
                <input value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Rol</label>
                <select value={form.role} onChange={e => setForm({...form, role: e.target.value})}>
                  <option value="admin">Administrador</option>
                  <option value="manager">Gerente</option>
                </select>
              </div>
              {form.role === 'manager' && (
                <div className="form-group">
                  <label>Tiendas Asignadas</label>
                  {stores.map(s => (
                    <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontWeight: 'normal' }}>
                      <input
                        type="checkbox"
                        checked={form.store_ids.includes(s.id)}
                        onChange={() => toggleStore(s.id)}
                        style={{ width: 'auto' }}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              )}
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
